import AddIcons from '@/icons/AddIcons'
import ConfigurationIcons from '@/icons/ConfigurationIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import {
    PRICING_MODELS,
    PRODUCT_UNITS,
    UNLIMITED,
} from '@/modules/Contract/constants'
import {
    Contract,
    Manufacture,
    Model,
    SingleProductConfigOptionProps,
} from '@/modules/Contract/types'
import { useMemo } from 'react'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
    Chip,
    GridHookForm,
    IconButton,
    Typogaphy,
    FormItem,
} from 'vnetwork-ui'
import { WAREHOUSE_SCOPE } from './constants'
import manufactures from './manufactures.json'
import models from './models.json'

interface WarehouseProps extends SingleProductConfigOptionProps {
    servicePriceModel?: string
}

const Warehouse: React.FC<WarehouseProps> = ({
    index,
    name,
    servicePriceModel = PRICING_MODELS.Package,
}) => {
    const formControl = useFormContext<Contract>()
    const { t } = useTranslation()

    const title = useWatch({
        control: formControl.control,
        name: `${name}.[${index}].name` as Path<Contract>,
    })

    const allValues = useWatch({
        control: formControl.control,
        name: name as Path<Contract>,
    })

    const defaultItemMax = useMemo(() => {
        if (!allValues || !Array.isArray(allValues)) return UNLIMITED
        const defaultItem = allValues.find(
            (item) =>
                item?.scope === WAREHOUSE_SCOPE.DEFAULT &&
                item?.name ===
                    formControl.getValues(
                        `${name}.[${index}].name` as Path<Contract>
                    )
        )
        return defaultItem?.max || UNLIMITED
    }, [allValues, index, name, formControl])

    const totalCapacity = useMemo(() => {
        if (!allValues || !Array.isArray(allValues)) return 0
        return allValues.reduce((sum: number, item: any) => {
            if (
                item?.name ===
                formControl.getValues(
                    `${name}.[${index}].name` as Path<Contract>
                )
            ) {
                const itemCapacity = Number(item?.capacity_value) || 0
                const itemQuantity = Number(item?.commitment_value) || 1
                return sum + itemCapacity * itemQuantity
            }
            return sum
        }, 0)
    }, [allValues, index, name, formControl])

    const warehouseTypeMapingName = {
        server: ['SERVER'],
        disk: ['SSD', 'HDD'],
        memory: ['RAM'],
    }

    const warehouseTypeMapingCapacity = {
        server: 'CPU',
        disk: 'CAPACITY',
        memory: 'CAPACITY',
    }

    const handleModelChange = (value: any) => {
        if (!value) {
            formControl.setValue(
                `${name}.[${index}].capacity_value` as Path<Contract>,
                null
            )

            formControl.setValue(
                `${name}.[${index}].brand_name` as Path<Contract>,
                null
            )

            formControl.setValue(
                `${name}.[${index}].service_tag` as Path<Contract>,
                null
            )

            formControl.setValue(
                `${name}.[${index}].capacity_unit` as Path<Contract>,
                null
            )

            return
        }

        const model = (models as Model[])?.find(
            (i) => i?.warehouse_model_name === value
        )

        const specification = model?.specifications?.find(
            (s) =>
                s.specification_name ===
                warehouseTypeMapingCapacity?.[
                    title as keyof typeof warehouseTypeMapingCapacity
                ]
        )

        const newCapacityValue = Number(specification?.specification_value || 0)

        // Check if adding this capacity would exceed the maximum from default item
        if (
            defaultItemMax !== UNLIMITED &&
            ['Disk', 'Memory'].includes(title as string)
        ) {
            const remainingCapacity = Number(defaultItemMax) - totalCapacity
            if (Number(newCapacityValue) > remainingCapacity) {
                return formControl.setError(
                    `${name}.[${index}].capacity_value` as Path<Contract>,
                    {
                        message: t('capacity_value_exceed_limit'),
                    }
                )
            }
        }

        formControl.setValue(
            `${name}.[${index}].capacity_value` as Path<Contract>,
            newCapacityValue
        )

        formControl.setValue(
            `${name}.[${index}].brand_name` as Path<Contract>,
            model?.manufacture?.manufacture_name
        )

        formControl.setValue(
            `${name}.[${index}].capacity_unit` as Path<Contract>,
            specification?.specification_unit?.toLowerCase()
        )
    }

    const currentCapacityValue =
        Number(
            formControl.getValues(
                `${name}.[${index}].capacity_value` as Path<Contract>
            )
        ) || 0
    const currentModelName = formControl.getValues(
        `${name}.[${index}].model_name` as Path<Contract>
    )

    const remainingQuota = useMemo(() => {
        if (
            defaultItemMax === UNLIMITED ||
            !['disk', 'memory'].includes(title as string)
        )
            return UNLIMITED

        const otherItemsCapacity = allValues.reduce(
            (sum: number, item: any) => {
                if (
                    item?.name ===
                        formControl.getValues(
                            `${name}.[${index}].name` as Path<Contract>
                        ) &&
                    index !== allValues.indexOf(item)
                ) {
                    const itemCapacity = Number(item?.capacity_value) || 0
                    const itemQuantity = Number(item?.commitment_value) || 1
                    return sum + itemCapacity * itemQuantity
                }
                return sum
            },
            0
        )

        return Number(defaultItemMax) - otherItemsCapacity
    }, [allValues, defaultItemMax, title, index, name, formControl])

    const commitmentOptions = useMemo(() => {
        if (!currentModelName || !currentCapacityValue) return []
        if (remainingQuota === UNLIMITED) {
            return Array.from({ length: 10 }, (_, i) => ({
                label: String(i + 1),
                value: i + 1,
            }))
        }

        const maxQuantity = Math.floor(remainingQuota / currentCapacityValue)
        return Array.from({ length: maxQuantity }, (_, i) => ({
            label: String(i + 1),
            value: i + 1,
        }))
    }, [currentModelName, currentCapacityValue, remainingQuota])

    const handleCommitmentChange = (value: any) => {
        if (!value || value === '') return true

        if (
            defaultItemMax !== UNLIMITED &&
            ['disk', 'memory'].includes(title as string)
        ) {
            const totalWithNewValue =
                Number(currentCapacityValue) * Number(value) +
                allValues.reduce((sum: number, item: any) => {
                    if (
                        item?.name ===
                            formControl.getValues(
                                `${name}.[${index}].name` as Path<Contract>
                            ) &&
                        index !== allValues.indexOf(item)
                    ) {
                        const itemCapacity = Number(item?.capacity_value) || 0
                        const itemQuantity = Number(item?.commitment_value) || 1
                        return sum + itemCapacity * itemQuantity
                    }
                    return sum
                }, 0)

            if (totalWithNewValue > Number(defaultItemMax)) {
                formControl.setError(
                    `${name}.[${index}].commitment_value` as Path<Contract>,
                    {
                        message: t('capacity_value_exceed_limit'),
                    }
                )
                return false
            }

            formControl.clearErrors(
                `${name}.[${index}].commitment_value` as Path<Contract>
            )
        }
        return true
    }

    const handleInsertWarehouse = () => {
        const newWarehouse = {
            ...allValues[index],
            commitment_value: 0,
            scope: WAREHOUSE_SCOPE.CLONE,
        }
        formControl.setValue(name as Path<Contract>, [
            ...(allValues?.slice(0, index + 1) || []),
            newWarehouse,
            ...(allValues?.slice(index + 1) || []),
        ])
    }

    const handleDeleteWarehouse = () => {
        const newWarehouses = allValues?.filter(
            (_: any, idx: number) => idx !== index
        )
        formControl.setValue(name as Path<Contract>, newWarehouses)
    }

    const isDefaultScope =
        formControl?.getValues(`${name}.[${index}].scope` as Path<Contract>) ===
        WAREHOUSE_SCOPE.DEFAULT

    const maxValue = formControl?.getValues(
        `${name}.[${index}].max` as Path<Contract>
    )

    const showQuota = !(isDefaultScope || maxValue === UNLIMITED || !maxValue)

    // Model options with filtering
    const modelOptions = useMemo(() => {
        return (models as Model[])
            ?.filter((i) => {
                const matchesType = warehouseTypeMapingName?.[
                    title as keyof typeof warehouseTypeMapingName
                ]?.includes(i?.warehouse_type?.warehouse_type_name)

                if (!matchesType || remainingQuota === UNLIMITED) {
                    return matchesType
                }

                const specification = i?.specifications?.find(
                    (s) =>
                        s.specification_name ===
                        warehouseTypeMapingCapacity?.[
                            title as keyof typeof warehouseTypeMapingCapacity
                        ]
                )

                const specValue =
                    Number(specification?.specification_value) || 0
                return matchesType && specValue <= remainingQuota
            })
            ?.map((i) => ({
                label: i?.warehouse_model_name,
                value: i?.warehouse_model_name,
            }))
    }, [title, remainingQuota])

    return (
        <div
            className="bg-white rounded border border-slate-200 hover:border-slate-300 transition-colors duration-200 mb-3"
            data-tour={`config-${title?.toLowerCase() || 'warehouse'}`}
        >
            {/* Header */}
            <div className="px-3 py-2.5 bg-slate-50/50 border-b border-slate-200/60">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <ConfigurationIcons className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <Typogaphy className="font-medium text-slate-800 text-sm truncate uppercase">
                                {t(
                                    formControl.getValues(
                                        `${name}.[${index}].name` as Path<Contract>
                                    )
                                )}
                            </Typogaphy>
                        </div>
                        {showQuota && (
                            <Chip
                                label={`${t('Quota')}: ${formControl.getValues(
                                    `${name}.[${index}].max` as Path<Contract>
                                )}`}
                                size="small"
                                color="primary"
                                variant="contained"
                                className="text-xs"
                            />
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={handleInsertWarehouse}
                            className="hover:bg-blue-50"
                        >
                            <AddIcons className="w-4 h-4" />
                        </IconButton>
                        {!isDefaultScope && (
                            <IconButton
                                color="error"
                                size="small"
                                onClick={handleDeleteWarehouse}
                                className="hover:bg-red-50 ml-1"
                            >
                                <DeleteOutlineIcons className="w-4 h-4" />
                            </IconButton>
                        )}
                    </div>
                </div>
            </div>

            {/* Configuration Fields */}
            <div className="px-3 py-2.5">
                <GridHookForm
                    subForm
                    formControl={formControl}
                    className="grid-cols-3"
                    data={[
                        {
                            renderEditComponent: (control) => (
                                <div
                                    data-tour={`warehouse-model-${title?.toLowerCase() || 'default'}`}
                                >
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].model_name` as Path<Contract>
                                        }
                                        type="select"
                                        label={t('Model')}
                                        placeholder={t('Model')}
                                        size="small"
                                        allowClear={false}
                                        options={modelOptions}
                                        onChangeAfterHookForm={(value) => {
                                            handleModelChange(value)
                                        }}
                                    />
                                </div>
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <div
                                    data-tour={`warehouse-manufacture-${title?.toLowerCase() || 'default'}`}
                                >
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].brand_name` as Path<Contract>
                                        }
                                        type="select"
                                        label={t('Manufacture')}
                                        placeholder={t('Manufacture')}
                                        size="small"
                                        disabled
                                        options={(
                                            manufactures as Manufacture[]
                                        )?.map((i) => ({
                                            label: i?.manufacture_name,
                                            value: i?.manufacture_name,
                                        }))}
                                    />
                                </div>
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <div
                                    data-tour={`warehouse-quantity-${title?.toLowerCase() || 'default'}`}
                                >
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].commitment_value` as Path<Contract>
                                        }
                                        type="select"
                                        label={t('quantity')}
                                        placeholder={t('quantity')}
                                        size="small"
                                        options={commitmentOptions}
                                        disabled={!currentModelName}
                                        allowClear={false}
                                        onChangeAfterHookForm={(value) => {
                                            handleCommitmentChange(value)
                                        }}
                                    />
                                </div>
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <div
                                    data-tour={`warehouse-price-${title?.toLowerCase() || 'default'}`}
                                >
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].commitment_price_per_unit` as Path<Contract>
                                        }
                                        type="number"
                                        label={t('commitment_price_per_unit')}
                                        placeholder={t(
                                            'commitment_price_per_unit'
                                        )}
                                        size="small"
                                    />
                                </div>
                            ),
                            className:
                                servicePriceModel === PRICING_MODELS.Package
                                    ? ''
                                    : 'hidden',
                        },
                        {
                            renderEditComponent: (control) => (
                                <div
                                    data-tour={`warehouse-capacity-${title?.toLowerCase() || 'default'}`}
                                >
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].capacity_value` as Path<Contract>
                                        }
                                        type="number"
                                        label={t('capacity_value')}
                                        placeholder={t('capacity_value')}
                                        size="small"
                                        disabled
                                    />
                                </div>
                            ),
                        },
                        {
                            renderEditComponent: (control) => (
                                <div
                                    data-tour={`warehouse-unit-${title?.toLowerCase() || 'default'}`}
                                >
                                    <FormItem
                                        control={control}
                                        name={
                                            `${name}.[${index}].capacity_unit` as Path<Contract>
                                        }
                                        type="select"
                                        label={t('unit of calculation')}
                                        placeholder={t('unit of calculation')}
                                        size="small"
                                        disabled
                                        options={PRODUCT_UNITS(t)}
                                    />
                                </div>
                            ),
                        },
                    ]}
                    actions={[]}
                />
            </div>
        </div>
    )
}

export default Warehouse
